// screens/AppendiceDomandeScreen.tsx — V28xFP host for the FP6 official-question
// browser. Route: /appendice/:argomentoId/domande.
//
// A thin V29-shell wrapper (Screen + TopChrome) around the FP6
// QuestionBrowser. It fetches the chapter (for sub-section grouping) via the
// consolidated appendiceApi.getChapter; if the chapter fails to load the browser
// degrades to a flat list (chapter=null). Chrome back arrow via the shared
// shell goBack — same nav contract as the rest of the Appendice.

import type { JSX } from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { useShellNav } from '../components/AppFrame';
import { useTokens } from '../state/theme';
import { appendiceApi, type AppendiceChapter } from '../api/appendice';
import { QuestionBrowser } from './appendice/QuestionBrowser';

export default function AppendiceDomandeScreen(): JSX.Element {
  const tokens = useTokens();
  const { goBack } = useShellNav();
  const { argomentoId = '' } = useParams<{ argomentoId: string }>();
  const navigate = useNavigate();

  const [chapter, setChapter] = useState<AppendiceChapter | null>(null);

  // Best-effort chapter fetch for sub-section grouping; QuestionBrowser falls
  // back to a flat list when chapter is null, so a failure is non-fatal.
  useEffect(() => {
    if (!argomentoId) return;
    let cancelled = false;
    appendiceApi
      .getChapter(argomentoId)
      .then((data) => { if (!cancelled) setChapter(data); })
      .catch(() => { if (!cancelled) setChapter(null); });
    return () => { cancelled = true; };
  }, [argomentoId]);

  const heading = chapter?.argomento_label || chapter?.sezione_title || 'Domande';

  return (
    <Screen>
      <TopChrome
        title={heading}
        subtitle="Domande"
        // Hierarchical back (2026-07-14 r4): one level up, deterministic.
        onBack={argomentoId ? () => navigate(`/appendice/${argomentoId}`, { replace: true }) : goBack}
      />

      <main
        className="asc-scroll"
        style={{
          flex:     1,
          overflow: 'auto',
          padding:  '16px 16px 32px',
          background: tokens.bg,
        }}
      >
        <QuestionBrowser argomentoId={argomentoId} chapter={chapter} />
      </main>
    </Screen>
  );
}
